/**
 * The product set card's summary (PLT-7586), shared by Upright web chat's card and the
 * `product-set-card` MCP app so the two list the same products.
 *
 * `get_dd_product_set` returns every row of the set, segments included. The summary lists products
 * only, the largest revenue share first, each under the segments it sits in, so a segment never
 * reads as a product and never counts towards the number of products.
 */

/** The fields of a `get_dd_product_set` product entry the summary reads. */
export interface ProductSetCardEntry {
  productId: string;
  productName: string;
  /** 0..1 ratio of the set's revenue; absent when the product's own weight was not entered. */
  revenueShare?: number;
  /** Set on a segment row: a container for products, not a product. */
  isSegment?: boolean;
  /** The id of the segment the row sits in; absent at the top level. */
  parentId?: string;
}

export interface ProductSetCardGroup<T> {
  /** Stable React key: the ids of the segments the products sit in. */
  key: string;
  /** Names of the segments the products sit in, outermost first; empty for top-level products. */
  segmentPath: string[];
  products: T[];
}

export interface ProductSetCardSummary<T> {
  /** Products in the set, segments not counted. */
  productCount: number;
  /** The products shown, grouped by segment, in the order their first product ranks. */
  groups: Array<ProductSetCardGroup<T>>;
  /** Products not shown. */
  hiddenCount: number;
}

export function summarizeProductSetCard<T extends ProductSetCardEntry>(
  entries: T[],
  limit: number,
): ProductSetCardSummary<T> {
  const byId = new Map(entries.map(entry => [entry.productId, entry]));
  // A row something sits in is a segment even without the flag: rows saved before every segment was
  // flagged carry none (see `isSegmentRow`).
  const parentIds = new Set(entries.flatMap(entry => (entry.parentId === undefined ? [] : [entry.parentId])));
  const products = entries.filter(entry => entry.isSegment !== true && !parentIds.has(entry.productId));
  const shown = [...products].sort((a, b) => (b.revenueShare ?? -1) - (a.revenueShare ?? -1)).slice(0, limit);

  // Keyed by the segments' ids, not their names: two segments may share a name.
  const groups = new Map<string, ProductSetCardGroup<T>>();
  for (const product of shown) {
    const segments = segmentsOf(product, byId);
    const key = segments.map(segment => segment.productId).join('/');
    const group = groups.get(key);
    if (group) {
      group.products.push(product);
    } else {
      groups.set(key, { key, segmentPath: segments.map(segment => segment.productName), products: [product] });
    }
  }
  return { productCount: products.length, groups: [...groups.values()], hiddenCount: products.length - shown.length };
}

/** The segments the entry sits in, outermost first. */
function segmentsOf(entry: ProductSetCardEntry, byId: Map<string, ProductSetCardEntry>): ProductSetCardEntry[] {
  const segments: ProductSetCardEntry[] = [];
  // `seen` stops at a cycle, which the editor refuses to save but an old set can still hold.
  const seen = new Set<string>([entry.productId]);
  let parent = entry.parentId === undefined ? undefined : byId.get(entry.parentId);
  while (parent && !seen.has(parent.productId)) {
    seen.add(parent.productId);
    segments.unshift(parent);
    parent = parent.parentId === undefined ? undefined : byId.get(parent.parentId);
  }
  return segments;
}
